import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatErr } from "@/lib/api";
import { Loader2, PawPrint } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden grain text-white" style={{ backgroundColor: "#1B2620" }}>
        <div className="absolute inset-0"
             style={{ backgroundImage: "radial-gradient(circle at 18% 28%, rgba(207,133,107,0.28), transparent 42%), radial-gradient(circle at 82% 72%, rgba(76,120,95,0.30), transparent 46%)" }} />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-primary text-primary-foreground"><PawPrint className="h-6 w-6" /></span>
            <span className="font-primary text-2xl font-bold">Pet Care Suite</span>
          </div>
          <div className="space-y-4 max-w-md">
            <h1 className="font-primary text-4xl font-bold leading-tight">One platform for consultations, grooming &amp; boarding.</h1>
            <p className="text-white/60 font-secondary">Multi-branch records, structured service bookings, instant PDF reports, and consolidated revenue insight.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md p-8 border-border/60 elevated">
          <div className="mb-8 lg:hidden flex items-center gap-2 font-primary text-xl font-bold">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground"><PawPrint className="h-5 w-5" /></span>
            Pet Care Suite
          </div>
          <h2 className="font-primary text-3xl font-bold tracking-tight">Welcome</h2>
          <p className="text-muted-foreground mt-1 text-sm">Sign in to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" autoComplete="email"
                     value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" autoComplete="current-password"
                     value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>

            {error && <div data-testid="login-error" className="text-sm text-destructive">{error}</div>}

            <Button data-testid="login-submit-btn" type="submit" disabled={busy}
                    className="w-full font-semibold">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign In
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
